import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Redirect } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Users, Mail, FileText, LogOut, Settings, BarChart3 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Link } from "wouter";
import type { BlogPost, User, Invite } from '@shared/schema';

export default function AdminDashboard() {
  const { user, logout, isAuthenticated, isAdmin, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isNewPostOpen, setIsNewPostOpen] = useState(false);
  const [isSendInviteOpen, setIsSendInviteOpen] = useState(false);
  const [isEditPostOpen, setIsEditPostOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // New Post Form State
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    slug: '',
    featuredImage: null as string | null
  });

  // Edit Post Form State
  const [editPost, setEditPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    slug: '',
    featuredImage: null as string | null
  });

  // Send Invite Form State
  const [inviteData, setInviteData] = useState({
    email: ''
  });

  // Always call hooks before any early returns
  const { data: blogPosts = [], isLoading: postsLoading } = useQuery<BlogPost[]>({
    queryKey: ['/api/admin/blog'],
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated && isAdmin
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['/api/admin/users'],
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated && isAdmin
  });

  const { data: invites = [], isLoading: invitesLoading } = useQuery<Invite[]>({
    queryKey: ['/api/admin/invites'],
    staleTime: 1000 * 60 * 5,
    enabled: isAuthenticated && isAdmin
  });

  // Create Post Mutation
  const createPostMutation = useMutation({
    mutationFn: async (postData: typeof newPost) => {
      return apiRequest('/api/admin/blog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog'] });
      toast({
        title: "Success",
        description: "Blog post created successfully"
      });
      setNewPost({ title: '', excerpt: '', content: '', slug: '', featuredImage: null });
      setIsNewPostOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create blog post",
        variant: "destructive"
      });
    }
  });

  // Update Post Mutation
  const updatePostMutation = useMutation({
    mutationFn: async ({ id, postData }: { id: string; postData: typeof editPost }) => {
      return apiRequest(`/api/admin/blog/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog'] });
      toast({
        title: "Success",
        description: "Blog post updated successfully"
      });
      setEditPost({ title: '', excerpt: '', content: '', slug: '', featuredImage: null });
      setIsEditPostOpen(false);
      setEditingPost(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update blog post",
        variant: "destructive"
      });
    }
  });

  // Delete Post Mutation
  const deletePostMutation = useMutation({
    mutationFn: async (postId: string) => {
      return apiRequest(`/api/admin/blog/${postId}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/blog'] });
      toast({
        title: "Success",
        description: "Blog post deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete blog post",
        variant: "destructive"
      });
    }
  });

  // Send Invite Mutation
  const sendInviteMutation = useMutation({
    mutationFn: async (inviteInfo: typeof inviteData) => {
      return apiRequest('/api/admin/invites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inviteInfo)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/invites'] });
      toast({
        title: "Success",
        description: "Invite sent successfully"
      });
      setInviteData({ email: '' });
      setIsSendInviteOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send invite",
        variant: "destructive"
      });
    }
  });

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated or not admin
  if (!isAuthenticated || !isAdmin) {
    return <Redirect to="/admin/login" />;
  }

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleCreatePost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }

    // Auto-generate slug if not provided
    const slug = newPost.slug || newPost.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    createPostMutation.mutate({
      ...newPost,
      slug,
      excerpt: newPost.excerpt || newPost.content.substring(0, 150) + '...'
    });
  };

  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteData.email.trim()) {
      toast({
        title: "Error",
        description: "Email is required",
        variant: "destructive"
      });
      return;
    }
    sendInviteMutation.mutate(inviteData);
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setEditPost({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      slug: post.slug,
      featuredImage: post.featuredImage
    });
    setIsEditPostOpen(true);
  };

  const handleUpdatePost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editPost.title.trim() || !editPost.content.trim() || !editingPost) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }

    updatePostMutation.mutate({
      id: editingPost.id,
      postData: {
        ...editPost,
        excerpt: editPost.excerpt || editPost.content.substring(0, 150) + '...'
      }
    });
  };

  const handleDeletePost = (postId: string, postTitle: string) => {
    if (window.confirm(`Are you sure you want to delete "${postTitle}"? This action cannot be undone.`)) {
      deletePostMutation.mutate(postId);
    }
  };

  const publishedPosts = blogPosts.filter(post => post.publishedAt !== null);
  const draftPosts = blogPosts.filter(post => post.publishedAt === null);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-3 sm:py-4 space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3 w-full sm:w-auto">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white truncate">
                Admin Dashboard
              </h1>
              <Badge variant="secondary" data-testid="badge-user-role" className="text-xs flex-shrink-0">
                {user?.role}
              </Badge>
            </div>

            <div className="flex items-center justify-between w-full sm:w-auto space-x-3">
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                Welcome, {user?.username}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                data-testid="button-logout"
                className="text-sm flex-shrink-0 bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden xs:inline">Logout</span>
                <span className="xs:hidden">Exit</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1 mb-6 h-auto p-1">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm px-2 py-3 sm:px-4">
              <Settings className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="hidden sm:inline">Overview</span>
              <span className="sm:hidden text-xs">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className="text-xs sm:text-sm px-2 py-3 sm:px-4">
              <FileText className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="hidden sm:inline">Blog Posts</span>
              <span className="sm:hidden text-xs">Blog</span>
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="text-xs sm:text-sm px-2 py-3 sm:px-4">
              <Users className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="text-xs sm:text-sm">Users</span>
            </TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites" className="text-xs sm:text-sm px-2 py-3 sm:px-4">
              <Mail className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
              <span className="text-xs sm:text-sm">Invites</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 pt-3">
                  <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="text-xl sm:text-2xl font-bold" data-testid="text-total-posts">
                    {postsLoading ? '...' : blogPosts.length}
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 pt-3">
                  <CardTitle className="text-sm font-medium">Published</CardTitle>
                  <Badge variant="default" className="text-xs flex-shrink-0">Live</Badge>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="text-xl sm:text-2xl font-bold text-green-600" data-testid="text-published-posts">
                    {postsLoading ? '...' : publishedPosts.length}
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 pt-3">
                  <CardTitle className="text-sm font-medium">Drafts</CardTitle>
                  <Badge variant="secondary" className="text-xs flex-shrink-0">Draft</Badge>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="text-xl sm:text-2xl font-bold text-orange-600" data-testid="text-draft-posts">
                    {postsLoading ? '...' : draftPosts.length}
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 pt-3">
                  <CardTitle className="text-sm font-medium">Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <div className="text-xl sm:text-2xl font-bold" data-testid="text-total-users">
                    {usersLoading ? '...' : users.length}
                  </div>
                </CardContent>
              </Card>

              <Link href="/admin/split-test">
                <Card className="hover:shadow-md transition-shadow cursor-pointer border-blood-red/20 hover:border-blood-red/50">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-3 pt-3">
                    <CardTitle className="text-sm font-medium">Split Testing</CardTitle>
                    <BarChart3 className="h-4 w-4 text-blood-red flex-shrink-0" />
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="text-xl sm:text-2xl font-bold text-blood-red">
                      Results
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest blog posts and user activity</CardDescription>
              </CardHeader>
              <CardContent>
                {postsLoading ? (
                  <div className="text-center py-4">Loading recent posts...</div>
                ) : (
                  <div className="space-y-4">
                    {blogPosts.slice(0, 5).map((post: BlogPost) => (
                      <div key={post.id} className="flex items-center justify-between py-2">
                        <div>
                          <p className="font-medium">{post.title}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : 'No date'}
                          </p>
                        </div>
                        <Badge variant={post.publishedAt ? 'default' : 'secondary'}>
                          {post.publishedAt ? 'Published' : 'Draft'}
                        </Badge>
                      </div>
                    ))}
                    {(!blogPosts || blogPosts.length === 0) && (
                      <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                        No blog posts yet. Create your first post!
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog" className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
              <div className="w-full sm:w-auto">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Blog Posts</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Manage your blog content</p>
              </div>
              <Dialog open={isNewPostOpen} onOpenChange={setIsNewPostOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="button-new-post" className="w-full sm:w-auto">
                    <Plus className="h-4 w-4 mr-2" />
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-full max-w-none sm:max-w-4xl max-h-screen sm:max-h-[90vh] overflow-y-auto bg-white text-black sm:rounded-lg rounded-none h-screen sm:h-auto">
                  <DialogHeader>
                    <DialogTitle className="text-black text-xl font-bold">Create New Blog Post</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePost} className="space-y-6">
                    <div>
                      <Label htmlFor="post-title" className="text-black font-semibold">Title *</Label>
                      <Input
                        id="post-title"
                        value={newPost.title}
                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                        placeholder="Enter post title"
                        required
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="post-slug" className="text-black font-semibold">Slug (optional)</Label>
                      <Input
                        id="post-slug"
                        value={newPost.slug}
                        onChange={(e) => setNewPost({ ...newPost, slug: e.target.value })}
                        placeholder="auto-generated-from-title"
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="post-excerpt" className="text-black font-semibold">Excerpt (optional)</Label>
                      <Textarea
                        id="post-excerpt"
                        value={newPost.excerpt}
                        onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                        placeholder="Brief description of the post"
                        rows={3}
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="post-featured-image" className="text-black font-semibold">Featured Image</Label>
                      <div className="space-y-3 mt-1">
                        <div>
                          <Label className="text-sm text-gray-600">Upload from Computer/Device</Label>
                          <Input
                            id="post-featured-image-file"
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Validate file type
                                const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                if (!validTypes.includes(file.type)) {
                                  toast({
                                    title: "Invalid file type",
                                    description: "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
                                    variant: "destructive"
                                  });
                                  e.target.value = '';
                                  return;
                                }

                                // Validate file size (max 2MB)
                                const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                                if (file.size > maxSize) {
                                  toast({
                                    title: "File too large",
                                    description: "Please select an image smaller than 2MB",
                                    variant: "destructive"
                                  });
                                  e.target.value = '';
                                  return;
                                }

                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  setNewPost({ ...newPost, featuredImage: e.target?.result as string });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="mt-1"
                            style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Or enter Image URL</Label>
                          <Input
                            id="post-featured-image-url"
                            type="url"
                            value={newPost.featuredImage || ''}
                            onChange={(e) => setNewPost({ ...newPost, featuredImage: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                            className="mt-1"
                            style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                          />
                        </div>
                        {newPost.featuredImage && (
                          <div className="mt-2">
                            <Label className="text-sm text-gray-600">Preview:</Label>
                            <img 
                              src={newPost.featuredImage} 
                              alt="Featured image preview" 
                              className="mt-1 max-w-xs max-h-32 object-cover rounded border"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="post-content">Content * (HTML supported)</Label>
                      <Textarea
                        id="post-content"
                        value={newPost.content}
                        onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                        placeholder="Write your blog post content here. HTML tags like <strong>, <em>, <a href=''>, <p>, etc. are supported"
                        rows={12}
                        required
                        className="font-mono text-sm"
                        style={{ 
                          backgroundColor: 'white', 
                          color: 'black', 
                          border: '2px solid #ccc',
                          minHeight: '300px'
                        }}
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        You can use HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href="..."&gt;, &lt;br&gt;, etc.
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-gray-100 rounded">
                      <p className="text-sm text-gray-600">
                        Posts are automatically published when saved. You can edit them later to change the publish date.
                      </p>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsNewPostOpen(false)}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={createPostMutation.isPending}
                        className="px-6 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {createPostMutation.isPending ? 'Creating...' : 'Create Post'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Edit Post Dialog */}
              <Dialog open={isEditPostOpen} onOpenChange={setIsEditPostOpen}>
                <DialogContent className="w-full max-w-none sm:max-w-4xl max-h-screen sm:max-h-[90vh] overflow-y-auto bg-white text-black sm:rounded-lg rounded-none h-screen sm:h-auto">
                  <DialogHeader>
                    <DialogTitle className="text-black text-xl font-bold">Edit Blog Post</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdatePost} className="space-y-6">
                    <div>
                      <Label htmlFor="edit-post-title" className="text-black font-semibold">Title *</Label>
                      <Input
                        id="edit-post-title"
                        value={editPost.title}
                        onChange={(e) => setEditPost({ ...editPost, title: e.target.value })}
                        placeholder="Enter post title"
                        required
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-post-slug" className="text-black font-semibold">Slug</Label>
                      <Input
                        id="edit-post-slug"
                        value={editPost.slug}
                        onChange={(e) => setEditPost({ ...editPost, slug: e.target.value })}
                        placeholder="auto-generated-from-title"
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-post-excerpt" className="text-black font-semibold">Excerpt</Label>
                      <Textarea
                        id="edit-post-excerpt"
                        value={editPost.excerpt}
                        onChange={(e) => setEditPost({ ...editPost, excerpt: e.target.value })}
                        placeholder="Brief description of the post"
                        rows={3}
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-post-featured-image" className="text-black font-semibold">Featured Image</Label>
                      <div className="space-y-3 mt-1">
                        <div>
                          <Label className="text-sm text-gray-600">Upload from Computer/Device</Label>
                          <Input
                            id="edit-post-featured-image-file"
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Validate file type
                                const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
                                if (!validTypes.includes(file.type)) {
                                  toast({
                                    title: "Invalid file type",
                                    description: "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
                                    variant: "destructive"
                                  });
                                  e.target.value = '';
                                  return;
                                }

                                // Validate file size (max 2MB)
                                const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                                if (file.size > maxSize) {
                                  toast({
                                    title: "File too large",
                                    description: "Please select an image smaller than 2MB",
                                    variant: "destructive"
                                  });
                                  e.target.value = '';
                                  return;
                                }

                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  setEditPost({ ...editPost, featuredImage: e.target?.result as string });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="mt-1"
                            style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-gray-600">Or enter Image URL</Label>
                          <Input
                            id="edit-post-featured-image-url"
                            type="url"
                            value={editPost.featuredImage || ''}
                            onChange={(e) => setEditPost({ ...editPost, featuredImage: e.target.value })}
                            placeholder="https://example.com/image.jpg"
                            className="mt-1"
                            style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                          />
                        </div>
                        {editPost.featuredImage && (
                          <div className="mt-2">
                            <Label className="text-sm text-gray-600">Preview:</Label>
                            <img 
                              src={editPost.featuredImage} 
                              alt="Featured image preview" 
                              className="mt-1 max-w-xs max-h-32 object-cover rounded border"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-post-content" className="text-black font-semibold">Content * (HTML supported)</Label>
                      <Textarea
                        id="edit-post-content"
                        value={editPost.content}
                        onChange={(e) => setEditPost({ ...editPost, content: e.target.value })}
                        placeholder="Write your blog post content here. HTML tags are supported"
                        rows={12}
                        required
                        className="font-mono text-sm"
                        style={{ 
                          backgroundColor: 'white', 
                          color: 'black', 
                          border: '2px solid #ccc',
                          minHeight: '300px'
                        }}
                      />
                      <p className="text-sm text-gray-600 mt-1">
                        You can use HTML tags: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;a href="..."&gt;, &lt;br&gt;, etc.
                      </p>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsEditPostOpen(false)}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={updatePostMutation.isPending}
                        className="px-6 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {updatePostMutation.isPending ? 'Updating...' : 'Update Post'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                {postsLoading ? (
                  <div className="text-center py-8">Loading blog posts...</div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {blogPosts.map((post: BlogPost) => (
                      <div key={post.id} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                          <div className="flex-1 min-w-0 pr-0 sm:pr-4">
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white truncate">
                              {post.title}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                              {post.excerpt}
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 text-xs sm:text-sm text-gray-500 space-y-1 sm:space-y-0">
                              <span className="truncate">Slug: {post.slug}</span>
                              <span className="flex-shrink-0">{post.createdAt ? new Date(post.createdAt).toLocaleDateString() : 'No date'}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end space-x-3 flex-shrink-0">
                            <Badge variant={post.publishedAt ? 'default' : 'secondary'} className="text-xs">
                              {post.publishedAt ? 'Published' : 'Draft'}
                            </Badge>
                            <div className="flex space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleEditPost(post)}
                                data-testid={`button-edit-${post.id}`}
                                className="text-xs px-3"
                              >
                                Edit
                              </Button>
                              <Button 
                                variant="destructive" 
                                size="sm" 
                                onClick={() => handleDeletePost(post.id, post.title)}
                                data-testid={`button-delete-${post.id}`}
                                className="text-xs px-3"
                                disabled={deletePostMutation.isPending}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!blogPosts || blogPosts.length === 0) && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No blog posts found. Create your first post!
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
                <p className="text-gray-600 dark:text-gray-400">Manage user roles and permissions</p>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                {usersLoading ? (
                  <div className="text-center py-8">Loading users...</div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map((userItem: User) => (
                      <div key={userItem.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              {userItem.username}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">{userItem.username}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={userItem.role === 'admin' ? 'default' : 'secondary'}>
                              {userItem.role}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!users || users.length === 0) && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No users found.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Invite Management</h2>
                <p className="text-gray-600 dark:text-gray-400">Send invites to new users</p>
              </div>
              <Dialog open={isSendInviteOpen} onOpenChange={setIsSendInviteOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="button-send-invite">
                    <Plus className="h-4 w-4 mr-2" />
                    Send Invite
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white text-black">
                  <DialogHeader>
                    <DialogTitle className="text-black text-xl font-bold">Send User Invite</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSendInvite} className="space-y-6">
                    <div>
                      <Label htmlFor="invite-email" className="text-black font-semibold">Email Address *</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteData.email}
                        onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                        placeholder="user@example.com"
                        required
                        className="mt-1"
                        style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}
                      />
                    </div>
                    <div>
                      <Label htmlFor="invite-role" className="text-black font-semibold">Role</Label>
                      <Select 
                        defaultValue="editor"
                        onValueChange={(value) => console.log('Role selected:', value)}
                      >
                        <SelectTrigger className="mt-1" style={{ backgroundColor: 'white', color: 'black', border: '2px solid #ccc' }}>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent className="bg-white text-black">
                          <SelectItem value="editor">Editor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsSendInviteOpen(false)}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={sendInviteMutation.isPending}
                        className="px-6 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {sendInviteMutation.isPending ? 'Sending...' : 'Send Invite'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                {invitesLoading ? (
                  <div className="text-center py-8">Loading invites...</div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {invites.map((invite: Invite) => (
                      <div key={invite.token} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              {invite.email}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                              Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant={invite.role === 'admin' ? 'default' : 'secondary'}>
                              {invite.role}
                            </Badge>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              data-testid={`button-revoke-${invite.token}`}
                            >
                              Revoke
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!invites || invites.length === 0) && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        No pending invites.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}